import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Divider,
  Alert,
  Stepper,
  Step,
  StepLabel,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp, query, where, limit, getDocs, doc, setDoc, onSnapshot } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || '');

const getToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const isTimeSlotAvailable = (slotValue: string, dateStr: string) => {
  if (!dateStr) return true;
  
  const today = getToday();
  const [y, m, d] = dateStr.split('-').map(Number);
  const selectedDate = new Date(y, m - 1, d);
  selectedDate.setHours(0, 0, 0, 0);

  if (selectedDate.getTime() > today.getTime()) return true;
  if (selectedDate.getTime() < today.getTime()) return false;

  // Es hoy
  const nowHour = new Date().getHours();
  const [startStr] = slotValue.split('-');
  const startHour = parseInt(startStr.split(':')[0], 10);
  
  return nowHour < startHour;
};

const steps = ['Información de Envío', 'Resumen del Pedido', 'Método de Pago'];

const generateOrderId = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 10; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

const PaymentForm = ({ onPaymentSuccess, onPaymentError, isProcessing, preparePendingOrder }: any) => {
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    const paymentElement = elements.getElement(PaymentElement);
    if (!paymentElement) {
      setErrorMessage('El formulario de pago no se ha cargado correctamente. Por favor recarga la página.');
      return;
    }

    try {
      if (typeof preparePendingOrder === 'function') {
        preparePendingOrder();
      }
    } catch {}

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.origin + '/orders/success',
      },
      redirect: 'if_required',
    });

    if (error) {
      setErrorMessage(error.message || 'Ocurrió un error con el pago.');
      onPaymentError(error.message);
    } else {
      onPaymentSuccess();
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement onReady={() => setIsReady(true)} />
      {errorMessage && <Alert severity="error" sx={{ mt: 2 }}>{errorMessage}</Alert>}
      <Button
        type="submit"
        variant="contained"
        fullWidth
        size="large"
        disabled={!stripe || isProcessing || !isReady}
        sx={{ mt: 3, mb: 2 }}
      >
        {isProcessing ? 'Procesando...' : 'Pagar Ahora'}
      </Button>
    </form>
  );
};

const CheckoutPage = () => {
  const { items, clearCart, getTotalPrice } = useCart();
  const totalPrice = getTotalPrice();
  const { user, addRecipient } = useAuth();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Cupón
  const [couponInput, setCouponInput] = useState('');
  const [couponApplied, setCouponApplied] = useState<any | null>(null);
  const [couponError, setCouponError] = useState('');
  const computeDiscount = (coupon: any, subtotal: number) => {
    const type = (coupon?.type || '').toString();
    if (type === 'free_shipping') return 0;

    const amount = Number(coupon?.amount || 0);
    if (!amount || amount <= 0) return 0;
    if (type === 'percentage') {
      const pct = Math.min(amount, 100);
      return Math.max(0, Math.round((subtotal * pct) / 100));
    }
    return Math.min(subtotal, Math.round(amount));
  };
  const shippingCost = couponApplied?.type === 'free_shipping' ? 0 : 99;
  const discountValue = couponApplied ? computeDiscount(couponApplied, totalPrice) : 0;
  const discountedTotal = Math.max(0, totalPrice - discountValue) + shippingCost;

  const handleApplyCoupon = async () => {
    setCouponError('');
    const code = couponInput.trim();
    if (!code) { setCouponError('Ingresa un código de cupón'); return; }
    try {
      const q = query(collection(db, 'coupons'), where('code', '==', code), where('active', '==', true), limit(1));
      const snap = await getDocs(q);
      if (snap.empty) { setCouponError('Cupón inválido o inactivo'); setCouponApplied(null); return; }
      const docData = snap.docs[0].data();
      setCouponApplied({ id: snap.docs[0].id, ...docData });
    } catch (e) {
      console.warn('[Checkout] Error validando cupón', e);
      setCouponError('No se pudo validar el cupón. Intenta de nuevo.');
    }
  };
  const handleRemoveCoupon = () => {
    setCouponApplied(null);
    setCouponInput('');
    setCouponError('');
  };

  const formatMXN = (value: number) =>
    new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value);

  const mexicoStates = [
    'Aguascalientes', 'Baja California', 'Baja California Sur', 'Campeche', 'Coahuila',
    'Colima', 'Chiapas', 'Chihuahua', 'Ciudad de México', 'Durango', 'Guanajuato',
    'Guerrero', 'Hidalgo', 'Jalisco', 'Estado de México', 'Michoacán', 'Morelos',
    'Nayarit', 'Nuevo León', 'Oaxaca', 'Puebla', 'Querétaro', 'Quintana Roo',
    'San Luis Potosí', 'Sinaloa', 'Sonora', 'Tabasco', 'Tamaulipas', 'Tlaxcala',
    'Veracruz', 'Yucatán', 'Zacatecas'
  ];

  const today = new Date();
  const minDate = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');

  // Shipping form
  const [shippingData, setShippingData] = useState({
    fullName: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    recipientName: '',
    recipientPhone: '',
    street: '',
    colonia: '',
    city: 'Atlixco',
    region: 'Puebla',
    postalCode: '',
    deliveryDate: '',
    deliveryTime: '',
    message: '',
    anonymous: false
  });
  const [selectedRecipientId, setSelectedRecipientId] = useState('');
  const [openSaveRecipientDialog, setOpenSaveRecipientDialog] = useState(false);

  // Payment form
  const [paymentMethod, setPaymentMethod] = useState('credit-card');
  const [paymentData, setPaymentData] = useState({
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: ''
  });

  // Card validation state
  const [cardType, setCardType] = useState<null | 'visa' | 'mastercard' | 'amex'>(null);
  const [cardError, setCardError] = useState(false);
  const [expiryError, setExpiryError] = useState(false);

  // Delivery Zones / Colonias logic
  const [zipEntries, setZipEntries] = useState<any[]>([]);
  const [availableColonias, setAvailableColonias] = useState<string[]>([]);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'settings', 'deliveryZones'), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        const entries = Array.isArray(data.zipCodeEntries) ? data.zipCodeEntries : [];
        setZipEntries(entries);
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const entry = zipEntries.find(e => String(e.zipCode).trim() === String(shippingData.postalCode).trim());
    if (entry && Array.isArray(entry.colonias) && entry.colonias.length > 0) {
      setAvailableColonias(entry.colonias);
      if (!entry.colonias.includes(shippingData.colonia)) {
        setShippingData(prev => ({ ...prev, colonia: '' }));
      }
    } else {
      setAvailableColonias([]);
    }
  }, [shippingData.postalCode, zipEntries]);

  // Helpers: Luhn check, card type detection, expiry validation
  const luhnCheck = (num: string): boolean => {
    const digits = num.replace(/\D/g, '');
    let sum = 0;
    let shouldDouble = false;
    for (let i = digits.length - 1; i >= 0; i--) {
      let d = parseInt(digits.charAt(i), 10);
      if (shouldDouble) {
        d *= 2;
        if (d > 9) d -= 9;
      }
      sum += d;
      shouldDouble = !shouldDouble;
    }
    return sum % 10 === 0;
  };

  const detectCardType = (num: string): null | 'visa' | 'mastercard' | 'amex' => {
    const first = num.trim()[0];
    if (first === '4') return 'visa';
    if (first === '5') return 'mastercard';
    if (first === '3') return 'amex';
    return null;
  };

  const validateExpiry = (value: string): boolean => {
    // Expect MM/YY, valid month 01-12, not past
    const match = value.match(/^(\d{2})\/(\d{2})$/);
    if (!match) return false;
    const month = parseInt(match[1], 10);
    const yearTwo = parseInt(match[2], 10);
    if (month < 1 || month > 12) return false;
    // Convert YY to 2000-based year
    const year = 2000 + yearTwo;
    const now = new Date();
    // Compare year-month against current
    const exp = new Date(year, month - 1, 1);
    const curr = new Date(now.getFullYear(), now.getMonth(), 1);
    return exp >= curr;
  };

  useEffect(() => {
    if (items.length === 0) {
      navigate('/cart');
    }
  }, [items, navigate]);

  const [clientSecret, setClientSecret] = useState('');
  const [paymentIntentLoading, setPaymentIntentLoading] = useState(false);
  const [paymentIntentError, setPaymentIntentError] = useState('');

  const stripeOptions = React.useMemo(() => ({ 
    clientSecret, 
    appearance: { theme: 'stripe' as const } 
  }), [clientSecret]);

  // Fetch PaymentIntent when on payment step
  useEffect(() => {
    if (activeStep === 2 && paymentMethod === 'credit-card') {
      setPaymentIntentLoading(true);
      setPaymentIntentError('');
      fetch('/api/create-payment-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: discountedTotal, currency: 'mxn' }),
      })
        .then((res) => {
          if (!res.ok) throw new Error('Error al conectar con el servidor de pagos');
          return res.json();
        })
        .then((data) => {
          if (data.error) throw new Error(data.error);
          if (!data.clientSecret) throw new Error('No se recibió el secreto del cliente');
          setClientSecret(data.clientSecret);
        })
        .catch((err) => {
          console.error('Error fetching payment intent:', err);
          setPaymentIntentError('No se pudo cargar el formulario de pago. Por favor intenta de nuevo o selecciona otro método.');
        })
        .finally(() => {
          setPaymentIntentLoading(false);
        });
    }
  }, [activeStep, paymentMethod, discountedTotal]);

  const preparePendingOrder = () => {
    try {
      const orderItems = items.map((it) => ({
        productId: it.product.id,
        name: it.product.name,
        price: it.product.price,
        quantity: it.quantity,
        image: it.product.image,
        category: it.product.category,
      }));
      const payload = {
        userId: user?.id,
        userName: user?.name,
        userEmail: user?.email,
        sender: {
          name: shippingData.fullName,
          email: shippingData.email,
          phone: shippingData.phone,
        },
        items: orderItems,
        subtotal: totalPrice,
        shippingCost,
        discount: discountValue,
        coupon: couponApplied ? { code: couponApplied.code, type: couponApplied.type, amount: couponApplied.amount } : null,
        total: discountedTotal,
        shipping: {
          recipientName: shippingData.recipientName,
          recipientPhone: shippingData.recipientPhone,
          street: shippingData.street,
          colonia: shippingData.colonia,
          city: shippingData.city,
          state: shippingData.region,
          zipCode: shippingData.postalCode,
          deliveryDate: shippingData.deliveryDate,
          deliveryTime: shippingData.deliveryTime,
          message: shippingData.message,
          anonymous: shippingData.anonymous,
        },
        payment: {
          method: paymentMethod,
          provider: 'stripe',
        },
      };
      localStorage.setItem('pendingOrder', JSON.stringify(payload));
    } catch {}
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      if (!shippingData.fullName || !shippingData.recipientName || !shippingData.recipientPhone || !shippingData.street || !shippingData.city || !shippingData.postalCode || !shippingData.deliveryDate) {
        setError('Por favor completa todos los campos requeridos de envío');
        return;
      }
    }
    if (activeStep === 2) {
      if (paymentMethod === 'paypal') {
         setError('PayPal no está implementado en este demo.');
         return;
      }
      if (paymentMethod === 'credit-card') {
        // Now using Stripe Elements, so we don't handle manual validation/submission here.
        // The PaymentForm component handles it.
        return; 
      }
    }
    setError('');
    setActiveStep((prev) => prev + 1);
  };

  const onStripePaymentSuccess = async () => {
     await handlePlaceOrder();
  };

  const onStripePaymentError = (msg: string) => {
    setError(msg);
    setLoading(false);
  };

  const handlePlaceOrder = async () => {
    setLoading(true);
    setError('');
    console.log('Iniciando proceso de orden con ID personalizado...');
    
    try {
      // Payment succeeded; persist order to Firestore if available
      try {
        if (db && user) {
          const orderItems = items.map((it) => ({
            productId: it.product.id,
            name: it.product.name,
            price: it.product.price,
            quantity: it.quantity,
            image: it.product.image,
            category: it.product.category,
            variantIndex: it.product.variantIndex ?? null,
            variantLabel: it.product.variantLabel || null,
          }));
          const newOrderId = generateOrderId();
          const orderPayload = {
            id: newOrderId,
            userId: user.id,
            userName: user.name,
            userEmail: user.email,
            sender: {
              name: shippingData.fullName,
              email: shippingData.email,
              phone: shippingData.phone,
            },
            items: orderItems,
            subtotal: totalPrice,
            shippingCost,
            discount: discountValue,
            coupon: couponApplied ? { code: couponApplied.code, type: couponApplied.type, amount: couponApplied.amount } : null,
            total: discountedTotal,
            shipping: {
              recipientName: shippingData.recipientName,
              recipientPhone: shippingData.recipientPhone,
              street: shippingData.street,
              colonia: shippingData.colonia,
              city: shippingData.city,
              state: shippingData.region,
              zipCode: shippingData.postalCode,
              deliveryDate: shippingData.deliveryDate,
              deliveryTime: shippingData.deliveryTime,
              message: shippingData.message,
              anonymous: shippingData.anonymous,
            },
            payment: {
              method: paymentMethod,
              provider: 'stripe',
            },
            status: 'paid',
            createdAt: serverTimestamp(),
          };
          await setDoc(doc(db, 'users', user.id, 'orders', newOrderId), orderPayload);
          const docRef = { id: newOrderId };
          try { localStorage.removeItem('pendingOrder'); } catch {}
          
          clearCart();
          navigate('/orders/success', { 
            state: { 
              orderId: docRef.id, 
              deliveryDate: shippingData.deliveryDate 
            } 
          });
          return;
        }
      } catch (persistErr) {
        console.warn('[Checkout] Error guardando pedido en Firestore', persistErr);
      }
      
      clearCart();
      navigate('/orders/success');
    } catch (err) {
      setError('Error al procesar el pedido. Por favor intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleShippingSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleNext();
  };

  const handlePaymentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handlePlaceOrder();
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Paper 
            elevation={0} 
            sx={{ 
              p: { xs: 3, md: 5 }, 
              borderRadius: 4, 
              boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
              backgroundColor: '#fff'
            }}
          >
            <Typography variant="h5" gutterBottom sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 600, mb: 3 }}>
              Información de Envío
            </Typography>
            
            <form onSubmit={handleShippingSubmit}>
              <Grid container spacing={3}>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    Datos del Remitente
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nombre Completo"
                    value={shippingData.fullName}
                    onChange={(e) => setShippingData({ ...shippingData, fullName: e.target.value })}
                    required
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Teléfono"
                    value={shippingData.phone}
                    onChange={(e) => setShippingData({ ...shippingData, phone: e.target.value })}
                    required
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="email"
                    label="Email"
                    value={shippingData.email}
                    onChange={(e) => setShippingData({ ...shippingData, email: e.target.value })}
                    required
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    Datos del Destinatario
                  </Typography>
                </Grid>
                {user?.recipients && user.recipients.length > 0 && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Destinatario guardado</InputLabel>
                      <Select
                        label="Destinatario guardado"
                        value={selectedRecipientId}
                        onChange={(e) => {
                          const id = e.target.value as string;
                          setSelectedRecipientId(id);
                          const rec = user?.recipients?.find(r => r.id === id) || null;
                          if (rec) {
                            setShippingData({
                              ...shippingData,
                              recipientName: rec.name,
                              recipientPhone: rec.phone || '',
                              street: rec.street,
                              colonia: rec.colonia || '',
                              city: 'Atlixco',
                              region: 'Puebla',
                              postalCode: rec.zipCode
                            });
                          }
                        }}
                        sx={{ borderRadius: 2 }}
                      >
                        <MenuItem value=""><em>Ninguno</em></MenuItem>
                        {user.recipients.map((r) => (
                          <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nombre del Destinatario"
                    value={shippingData.recipientName}
                    onChange={(e) => setShippingData({ ...shippingData, recipientName: e.target.value })}
                    required
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Teléfono del Destinatario"
                    value={shippingData.recipientPhone}
                    onChange={(e) => setShippingData({ ...shippingData, recipientPhone: e.target.value })}
                    required
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Código Postal"
                  value={shippingData.postalCode}
                  onChange={(e) => setShippingData({ ...shippingData, postalCode: e.target.value })}
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                {availableColonias.length > 0 ? (
                  <FormControl fullWidth required disabled={!shippingData.postalCode || shippingData.postalCode.length < 5}>
                    <InputLabel>Colonia</InputLabel>
                    <Select
                      value={shippingData.colonia}
                      label="Colonia"
                      onChange={(e) => setShippingData({ ...shippingData, colonia: e.target.value })}
                      sx={{ borderRadius: 2 }}
                    >
                      {availableColonias.map((c) => (
                        <MenuItem key={c} value={c}>
                          {c}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    fullWidth
                    label="Colonia"
                    value={shippingData.colonia}
                    onChange={(e) => setShippingData({ ...shippingData, colonia: e.target.value })}
                    required
                    disabled={!shippingData.postalCode || shippingData.postalCode.length < 5}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Calle y Número"
                  value={shippingData.street}
                  onChange={(e) => setShippingData({ ...shippingData, street: e.target.value })}
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Ciudad"
                  value={shippingData.city}
                  required
                  disabled
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Estado"
                  value={shippingData.region}
                  required
                  disabled
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Fecha de Entrega"
                    value={shippingData.deliveryDate}
                    onChange={(e) => setShippingData({ ...shippingData, deliveryDate: e.target.value })}
                    required
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: minDate }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Horario de Entrega</InputLabel>
                    <Select
                      value={shippingData.deliveryTime}
                      onChange={(e) => setShippingData({ ...shippingData, deliveryTime: e.target.value })}
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="9:00-12:00" disabled={!isTimeSlotAvailable('9:00-12:00', shippingData.deliveryDate)}>Mañana (9:00 - 12:00)</MenuItem>
                      <MenuItem value="14:00-18:00" disabled={!isTimeSlotAvailable('14:00-18:00', shippingData.deliveryDate)}>Tarde (14:00 - 18:00)</MenuItem>
                      <MenuItem value="18:00-21:00" disabled={!isTimeSlotAvailable('18:00-21:00', shippingData.deliveryDate)}>Noche (18:00 - 21:00)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Mensaje para la tarjeta (opcional)"
                    value={shippingData.message}
                    onChange={(e) => setShippingData({ ...shippingData, message: e.target.value })}
                    placeholder="Escribe un mensaje personalizado para el destinatario..."
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={shippingData.anonymous}
                        onChange={(e) => setShippingData({ ...shippingData, anonymous: e.target.checked })}
                      />
                    }
                    label="Envío anónimo (ocultar nombre del remitente)"
                  />
                  <Typography variant="caption" sx={{ display: 'block', color: '#666' }}>
                    Si activas esta opción, tu nombre no aparecerá en la entrega ni en la tarjeta.
                  </Typography>
                </Grid>
              </Grid>

              <Box sx={{ mt: 5, display: 'flex', justifyContent: 'space-between' }}>
                <Button 
                  variant="outlined" 
                  onClick={() => navigate('/cart')}
                  sx={{ 
                    borderRadius: '50px', 
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 4
                  }}
                >
                  Volver al Carrito
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  sx={{
                    backgroundColor: 'primary.main',
                    '&:hover': { backgroundColor: 'primary.dark' },
                    borderRadius: '50px', 
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 4,
                    boxShadow: '0 4px 15px rgba(233, 30, 99, 0.3)'
                  }}
                >
                  Continuar al Resumen
                </Button>
              </Box>
              <Dialog open={openSaveRecipientDialog} onClose={() => setOpenSaveRecipientDialog(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontFamily: '"Playfair Display", serif' }}>Guardar destinatario</DialogTitle>
                <DialogContent>
                  <Typography>¿Deseas guardar este destinatario para futuras compras?</Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                  <Button onClick={() => { setOpenSaveRecipientDialog(false); setActiveStep(1); }} sx={{ borderRadius: '50px', textTransform: 'none' }}>No, continuar</Button>
                  <Button
                    variant="contained"
                    onClick={async () => {
                      try {
                        await addRecipient({
                          name: shippingData.recipientName,
                          street: shippingData.street,
                          colonia: shippingData.colonia || '',
                          city: shippingData.city,
                          state: shippingData.region,
                          zipCode: shippingData.postalCode,
                          phone: shippingData.recipientPhone
                        });
                        setOpenSaveRecipientDialog(false);
                        setActiveStep(1);
                      } catch (err) {
                        setOpenSaveRecipientDialog(false);
                        setError('Error al guardar el destinatario');
                        setActiveStep(1);
                      }
                    }}
                    sx={{ borderRadius: '50px', textTransform: 'none', boxShadow: '0 4px 15px rgba(233, 30, 99, 0.3)' }}
                  >
                    Sí, guardar
                  </Button>
                </DialogActions>
              </Dialog>
            </form>
          </Paper>
        );

      case 2:
        return (
          <Paper 
            elevation={0} 
            sx={{ 
              p: { xs: 3, md: 5 }, 
              borderRadius: 4, 
              boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
              backgroundColor: '#fff'
            }}
          >
            <Typography variant="h5" gutterBottom sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 600, mb: 3, color: '#333' }}>
              Método de Pago
            </Typography>
            <Alert severity="info" sx={{ mb: 4, borderRadius: 2 }}>
              Total a cobrar: <strong>{formatMXN(discountedTotal)}</strong>
            </Alert>

            {paymentMethod === 'transferencia' ? (
              <form onSubmit={handlePaymentSubmit}>
                <FormControl component="fieldset" sx={{ mb: 4, width: '100%' }}>
                  <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>Selecciona tu método de pago</FormLabel>
                  <RadioGroup
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  >
                    <FormControlLabel 
                      value="credit-card" 
                      control={<Radio sx={{ '&.Mui-checked': { color: 'primary.main' } }} />} 
                      label="Tarjeta de Crédito/Débito" 
                      sx={{ 
                        border: '1px solid #eee', 
                        borderRadius: 2, 
                        m: 0, 
                        mb: 2, 
                        p: 1, 
                        width: '100%',
                        '&:hover': { backgroundColor: '#fafafa' }
                      }} 
                    />
                    <FormControlLabel 
                      value="transferencia" 
                      control={<Radio sx={{ '&.Mui-checked': { color: 'primary.main' } }} />} 
                      label="Transferencia Bancaria" 
                      sx={{ 
                        border: '1px solid #eee', 
                        borderRadius: 2, 
                        m: 0, 
                        p: 1, 
                        width: '100%',
                        backgroundColor: '#f9f9f9',
                        borderColor: 'primary.main'
                      }} 
                    />
                  </RadioGroup>
                </FormControl>

                <Box sx={{ p: 3, backgroundColor: '#f5f5f5', borderRadius: 3, mb: 4 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    Datos para transferencia:
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
                    <strong>Banco:</strong> Banco<br />
                    <strong>Cuenta:</strong> 1234567890<br />
                    <strong>Nombre:</strong> Blüm
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 2, color: 'text.secondary' }}>
                    *Envía el comprobante a info@floraldelivery.cl
                  </Typography>
                  <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
                    <Button 
                      variant="outlined" 
                      onClick={() => setActiveStep(1)}
                      sx={{ 
                        borderRadius: '50px', 
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 4
                      }}
                    >
                      Volver
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      sx={{
                        backgroundColor: 'primary.main',
                        '&:hover': { backgroundColor: 'primary.dark' },
                        borderRadius: '50px', 
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 4,
                        boxShadow: '0 4px 15px rgba(233, 30, 99, 0.3)'
                      }}
                    >
                      Confirmar Pedido
                    </Button>
                  </Box>
                </Box>
              </form>
            ) : (
              <>
                <FormControl component="fieldset" sx={{ mb: 4, width: '100%' }}>
                  <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>Selecciona tu método de pago</FormLabel>
                  <RadioGroup
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  >
                    <FormControlLabel 
                      value="credit-card" 
                      control={<Radio sx={{ '&.Mui-checked': { color: 'primary.main' } }} />} 
                      label="Tarjeta de Crédito/Débito" 
                      sx={{ 
                        border: '1px solid #eee', 
                        borderRadius: 2, 
                        m: 0, 
                        mb: 2, 
                        p: 1, 
                        width: '100%',
                        backgroundColor: '#f9f9f9',
                        borderColor: 'primary.main'
                      }} 
                    />
                    <FormControlLabel 
                      value="transferencia" 
                      control={<Radio sx={{ '&.Mui-checked': { color: 'primary.main' } }} />} 
                      label="Transferencia Bancaria" 
                      sx={{ 
                        border: '1px solid #eee', 
                        borderRadius: 2, 
                        m: 0, 
                        p: 1, 
                        width: '100%',
                        '&:hover': { backgroundColor: '#fafafa' }
                      }} 
                    />
                  </RadioGroup>
                </FormControl>
                
                {paymentIntentLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                     <Typography>Cargando formulario de pago...</Typography>
                  </Box>
                )}

                {paymentIntentError && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {paymentIntentError}
                    <Button color="inherit" size="small" onClick={() => setActiveStep(1)}>
                      Volver
                    </Button>
                  </Alert>
                )}

                {clientSecret && !paymentIntentLoading && (
                  <Box sx={{ mb: 3 }}>
                    <Elements stripe={stripePromise} options={stripeOptions}>
                       <PaymentForm 
                          onPaymentSuccess={onStripePaymentSuccess} 
                          onPaymentError={onStripePaymentError}
                          isProcessing={loading}
                          preparePendingOrder={preparePendingOrder}
                       />
                    </Elements>
                    <Button 
                      variant="outlined" 
                      onClick={() => setActiveStep(1)} 
                      sx={{ 
                        mt: 4,
                        borderRadius: '50px', 
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 4
                      }}
                    >
                      Volver
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Paper>
        );

      case 1:
        return (
          <Paper 
            elevation={0} 
            sx={{ 
              p: { xs: 3, md: 5 }, 
              borderRadius: 4, 
              boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
              backgroundColor: '#fff'
            }}
          >
            <Typography variant="h5" gutterBottom sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 600, mb: 3 }}>
              Resumen del Pedido
            </Typography>

            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'text.secondary' }}>
                Productos
              </Typography>
              {items.map((item) => (
                <Box key={item.product.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 2, alignItems: 'center', borderBottom: '1px solid #f0f0f0' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box 
                      component="img" 
                      src={item.product.image} 
                      alt={item.product.name}
                      sx={{ width: 60, height: 60, borderRadius: 2, objectFit: 'cover', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                    />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {item.product.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Cantidad: {item.quantity}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatMXN(item.product.price * item.quantity)}
                  </Typography>
                </Box>
              ))}
              
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', my: 3 }}>
                <TextField
                  label="Código de cupón"
                  size="small"
                  value={couponInput}
                  onChange={(e) => setCouponInput(e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <Button 
                  variant="outlined" 
                  size="small" 
                  onClick={handleApplyCoupon}
                  sx={{ borderRadius: '20px', textTransform: 'none' }}
                >
                  Aplicar
                </Button>
                {couponApplied && (
                  <Button 
                    variant="text" 
                    size="small" 
                    color="error" 
                    onClick={handleRemoveCoupon}
                    sx={{ borderRadius: '20px', textTransform: 'none' }}
                  >
                    Quitar
                  </Button>
                )}
              </Box>
              {couponError && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{couponError}</Alert>}
              
              <Box sx={{ backgroundColor: '#f9f9f9', p: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                  <Typography variant="body2">Subtotal:</Typography>
                  <Typography variant="body2">{formatMXN(totalPrice)}</Typography>
                </Box>
                {discountValue > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                    <Typography variant="body2" color="primary">Descuento ({couponApplied?.code}):</Typography>
                    <Typography variant="body2" color="primary">- {formatMXN(discountValue)}</Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                  <Typography variant="body2">Envío:</Typography>
                  <Typography variant="body2">{formatMXN(shippingCost)}</Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Total:</Typography>
                  <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700 }}>
                    {formatMXN(Math.max(0, totalPrice - discountValue) + shippingCost)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Grid container spacing={4} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  Información de Envío
                </Typography>
                <Box sx={{ p: 2, border: '1px solid #eee', borderRadius: 2 }}>
                  <Typography variant="body2" paragraph>
                    <strong>Remitente:</strong> {shippingData.anonymous ? 'Envío anónimo' : shippingData.fullName}<br />
                    <strong>Destinatario:</strong> {shippingData.recipientName}<br />
                    <strong>Dirección:</strong> {shippingData.street}, Col. {shippingData.colonia}, {shippingData.city}, {shippingData.region} {shippingData.postalCode}<br />
                    <strong>Teléfono:</strong> {shippingData.phone} (Remitente) / {shippingData.recipientPhone} (Destinatario)
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                 <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  Detalles de Entrega
                </Typography>
                <Box sx={{ p: 2, border: '1px solid #eee', borderRadius: 2 }}>
                  <Typography variant="body2" paragraph>
                    <strong>Fecha:</strong> {shippingData.deliveryDate}<br />
                    <strong>Horario:</strong> {shippingData.deliveryTime}
                  </Typography>
                  {shippingData.message && (
                    <Typography variant="body2">
                      <strong>Mensaje:</strong> "{shippingData.message}"
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button 
                variant="outlined" 
                onClick={() => setActiveStep(0)}
                sx={{ 
                  borderRadius: '50px', 
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 4
                }}
              >
                Volver
              </Button>
              <Button
                variant="contained"
                onClick={() => setActiveStep(2)}
                size="large"
                sx={{
                  backgroundColor: 'primary.main',
                  '&:hover': { backgroundColor: 'primary.dark' },
                  borderRadius: '50px', 
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 4,
                  boxShadow: '0 4px 15px rgba(233, 30, 99, 0.3)'
                }}
              >
                Continuar a Método de Pago
              </Button>
            </Box>
          </Paper>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {error && <Alert severity="error" sx={{ mb: 4, borderRadius: 2 }}>{error}</Alert>}

      <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, mb: 4, color: '#333' }}>
        Finalizar Compra
      </Typography>

      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 6 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel StepIconProps={{ sx: { '&.Mui-active': { color: 'primary.main' }, '&.Mui-completed': { color: 'primary.main' } } }}>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {getStepContent(activeStep)}
    </Container>
  );
};

export default CheckoutPage;
